import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import { format, differenceInMonths } from 'date-fns';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

function BadgeCard({ badge, earned, earnedDate }) {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div
      className={`relative bg-[var(--color-card)] border rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-sm)] flex flex-col items-center gap-2 text-center transition-all ${
        earned ? 'border-[var(--color-primary)]/40 hover:border-[var(--color-primary)]' : 'border-[var(--color-border)] opacity-50 grayscale'
      }`}
      onMouseEnter={() => !earned && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {!earned && (
        <div className="absolute top-2 right-2">
          <Icon name="Lock" size={12} color="var(--color-muted-foreground)" />
        </div>
      )}
      <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${
        earned ? 'bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20' : 'bg-[var(--color-muted)]'
      }`}>
        {badge?.emoji}
      </div>
      <div>
        <p className="text-xs font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">{badge?.name}</p>
        {earned && earnedDate && (
          <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
            {format(new Date(earnedDate), 'MMM d, yyyy')}
          </p>
        )}
      </div>
      {/* Tooltip for locked badges */}
      {showTooltip && !earned && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 bg-[var(--color-foreground)] text-[var(--color-background)] text-xs rounded-[var(--radius-sm)] px-3 py-2 whitespace-nowrap shadow-lg font-[var(--font-caption)]">
          {badge?.condition}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[var(--color-foreground)]" />
        </div>
      )}
    </div>
  );
}

export default function AchievementBadges({ memberId, memberCreatedAt }) {
  const [attendances, setAttendances] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [personalRecords, setPersonalRecords] = useState([]);
  const [goal, setGoal] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: attData } = await api.get('/attendances', { params: { member_id: memberId, limit: 2000 } });
      setAttendances(attData ?? []);
      setWorkoutLogs([]);
      setPersonalRecords([]);
      setGoal(null);
      setMeasurements([]);
    } catch (e) {
      setError(e?.message ?? 'Failed to load badge data');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Attendance streak calculation
  const attDates = [...new Set(attendances?.map(a => format(new Date(a?.check_in_time), 'yyyy-MM-dd')))]?.sort();
  let maxStreak = 0;
  let tempStreak = 1;
  for (let i = 1; i < attDates?.length; i++) {
    const prev = new Date(attDates?.[i - 1]);
    const curr = new Date(attDates?.[i]);
    const diff = Math.round((curr - prev) / 86400000);
    if (diff === 1) {
      tempStreak++;
      if (tempStreak > maxStreak) maxStreak = tempStreak;
    } else {
      tempStreak = 1;
    }
  }
  if (attDates?.length === 1) maxStreak = 1;

  const monthsAsMember = memberCreatedAt ? differenceInMonths(new Date(), new Date(memberCreatedAt)) : 0;
  const latestWeight = measurements?.[0]?.weight_kg;
  const goalReached = goal?.target_weight && latestWeight && latestWeight <= goal?.target_weight;

  const BADGES = [
    {
      id: 'first_checkin', name: 'First Check-in', emoji: '🏃',
      condition: 'Complete your first gym check-in',
      earned: attendances?.length > 0,
      earnedDate: attendances?.[0]?.check_in_time,
    },
    {
      id: 'streak_7', name: '7-Day Streak', emoji: '🔥',
      condition: 'Attend the gym 7 days in a row',
      earned: maxStreak >= 7,
      earnedDate: maxStreak >= 7 ? attDates?.[6] : null,
    },
    {
      id: 'streak_30', name: '30-Day Streak', emoji: '⚡',
      condition: 'Attend the gym 30 days in a row',
      earned: maxStreak >= 30,
      earnedDate: maxStreak >= 30 ? attDates?.[29] : null,
    },
    {
      id: 'first_workout', name: 'First Workout', emoji: '💪',
      condition: 'Complete your first logged workout',
      earned: workoutLogs?.length > 0,
      earnedDate: workoutLogs?.[0]?.completed_at,
    },
    {
      id: 'workouts_10', name: '10 Workouts', emoji: '🏋️',
      condition: 'Complete 10 workouts',
      earned: workoutLogs?.length >= 10,
      earnedDate: workoutLogs?.[9]?.completed_at,
    },
    {
      id: 'workouts_50', name: '50 Workouts', emoji: '🥇',
      condition: 'Complete 50 workouts',
      earned: workoutLogs?.length >= 50,
      earnedDate: workoutLogs?.[49]?.completed_at,
    },
    {
      id: 'workouts_100', name: '100 Workouts', emoji: '🏆',
      condition: 'Complete 100 workouts',
      earned: workoutLogs?.length >= 100,
      earnedDate: workoutLogs?.[99]?.completed_at,
    },
    {
      id: 'first_pr', name: 'First PR', emoji: '📈',
      condition: 'Log your first personal record',
      earned: personalRecords?.length > 0,
      earnedDate: personalRecords?.[0]?.achieved_at,
    },
    {
      id: 'goal_weight', name: 'Goal Weight', emoji: '🎯',
      condition: `Reach your target weight goal`,
      earned: !!goalReached,
      earnedDate: goalReached ? measurements?.[0]?.recorded_at : null,
    },
    {
      id: 'member_3mo', name: '3-Month Member', emoji: '🌱',
      condition: 'Be a member for 3 months',
      earned: monthsAsMember >= 3,
      earnedDate: memberCreatedAt ? new Date(new Date(memberCreatedAt).setMonth(new Date(memberCreatedAt).getMonth() + 3))?.toISOString() : null,
    },
    {
      id: 'member_6mo', name: '6-Month Member', emoji: '⭐',
      condition: 'Be a member for 6 months',
      earned: monthsAsMember >= 6,
      earnedDate: memberCreatedAt ? new Date(new Date(memberCreatedAt).setMonth(new Date(memberCreatedAt).getMonth() + 6))?.toISOString() : null,
    },
    {
      id: 'member_1yr', name: '1-Year Member', emoji: '🎖️',
      condition: 'Be a member for 1 year',
      earned: monthsAsMember >= 12,
      earnedDate: memberCreatedAt ? new Date(new Date(memberCreatedAt).setFullYear(new Date(memberCreatedAt).getFullYear() + 1))?.toISOString() : null,
    },
  ];

  const earnedCount = BADGES?.filter(b => b?.earned)?.length;

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8]?.map(i => <SkeletonBlock key={i} className="h-36" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-md)]">
        <Icon name="AlertCircle" size={16} color="var(--color-error)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header KPI */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)] flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--color-warning)]/20 to-[var(--color-accent)]/20 flex items-center justify-center text-2xl flex-shrink-0">
          🏆
        </div>
        <div>
          <p className="text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
            {earnedCount} <span className="text-base font-medium text-[var(--color-muted-foreground)]">/ {BADGES?.length}</span>
          </p>
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Badges earned</p>
        </div>
        <div className="ml-auto hidden sm:block">
          <div className="h-2 w-40 bg-[var(--color-muted)] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] rounded-full transition-all duration-700"
              style={{ width: `${Math.round((earnedCount / BADGES?.length) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1 text-right">
            {Math.round((earnedCount / BADGES?.length) * 100)}% complete
          </p>
        </div>
      </div>

      {/* Badge Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {BADGES?.map(badge => (
          <BadgeCard key={badge?.id} badge={badge} earned={badge?.earned} earnedDate={badge?.earnedDate} />
        ))}
      </div>
    </div>
  );
}
